import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import '../style.css';
import '../nov.css';

const SuccessStories = () => {
  const stories = [
    {
      title: "Independent Folk Artist",
      description: "Media Nov helped me reach 2M+ streams across all platforms in just 6 months. Their rights management is exceptional.",
      badges: [
        { text: "+250% Growth", color: "green" },
        { text: "Global Reach", color: "blue" }
      ],
      icon: (
        <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M9 18V5l12-1v13M9 10l12-1M9 18c0 1.657-1.343 3-3 3s-3-1.343-3-3 1.343-3 3-3 3 1.343 3 3zm12-3c0 1.657-1.343 3-3 3s-3-1.343-3-3 1.343-3 3-3 3 1.343 3 3z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-black"></path>
        </svg>
      )
    },
    {
      title: "Electronic Music Producer",
      description: "The analytics and distribution network are incredible. I've seen my revenue increase by 400% since joining Media Nov.",
      badges: [
        { text: "+400% Revenue", color: "green" },
        { text: "50+ Platforms", color: "purple" }
      ],
      icon: (
        <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M13 2L3 14h8l-2 8 10-12h-8l2-8z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-black"></path>
        </svg>
      )
    },
    {
      title: "Singer-Songwriter",
      description: "Their rights protection saved me thousands in potential losses. Professional service with personal touch.",
      badges: [
        { text: "Rights Protected", color: "green" },
        { text: "$10K+ Saved", color: "yellow" }
      ],
      icon: (
        <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M20 11V7.132c0-.446-.002-.669-.024-.863a2 2 0 00-.317-.8c-.13-.183-.3-.338-.642-.647l-5.333-4.8c-.278-.25-.417-.375-.569-.456a2 2 0 00-.63-.174C12.313 0 12.134 0 11.778 0h-.112c-.357 0-.535 0-.707.058a2 2 0 00-.63.174c-.152.081-.29.206-.569.456l-5.333 4.8c-.341.309-.512.464-.642.647a2 2 0 00-.317.8C4 7.463 4 7.686 4 8.132V11c0 5.523 4.477 10 8 10s8-4.477 8-10z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-black"></path>
          <path d="M9 11l2 2 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-black"></path>
        </svg>
      )
    }
  ];

  useEffect(() => {
    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'Media Nov Success Stories',
      description:
        'Real success stories from independent artists who grew streams, revenue, and rights protection with Media Nov.',
      itemListElement: stories.map((story, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Review',
          author: { '@type': 'Person', name: story.title },
          reviewBody: story.description,
          itemReviewed: {
            '@type': 'Organization',
            name: 'Media Nov',
            url: 'https://medianov.com',
          },
        },
      })),
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'success-stories-schema';
    script.textContent = JSON.stringify(structuredData);
    document.head.appendChild(script);

    return () => {
      document.getElementById('success-stories-schema')?.remove();
    };
  }, []);

  const getBadgeColor = (color) => {
    const colors = {
      green: "icon-gradient",
      blue: "icon-gradient",
      purple: "icon-gradient",
      yellow: "icon-gradient"
    };
    return colors[color] || colors.green;
  };

  return (
    <section id="success-stories" className="bg-[#f5f5f7] py-12 lg:py-20 relative overflow-hidden" style={{ fontFamily: 'aesthet-nova, Helvetica, Arial' }}>
      <div className="container mx-auto px-4 lg:px-0 relative z-10">
        <div className="w-full flex flex-col items-center justify-center my-12">
          
          {/* Header */}
          <div className="w-full text-center mb-16">
            <motion.div 
              className="mb-8 mt-4 text-center"
              initial={{ opacity: 0, y: -20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-sm font-medium badge-gradient text-gray-700 py-3 px-6 rounded-full inline-block uppercase tracking-wide" style={{ fontFamily: 'aesthet-nova' }}>
                Success Stories That Inspire
              </span>
            </motion.div>
            
            <motion.h2 
              className="text-3xl md:text-5xl lg:text-6xl font-bold mb-8 relative" 
              style={{ fontFamily: 'aesthet-nova', fontWeight: 700 }}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="flex items-center justify-center gap-3">
                <motion.span 
                  style={{ color: 'rgb(70, 52, 135)' }}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                >
                  EMPOWERING
                </motion.span>
                <motion.span 
                  style={{ '--shimmer-width': '250px' }} 
                  className="text-neutral-600/50 animate-shimmer bg-clip-text bg-no-repeat [background-position:0_0] [background-size:var(--shimmer-width)_100%] [transition:background-position_1s_cubic-bezier(.6,.6,0,1)_infinite] bg-gradient-to-r from-neutral-100 via-black/80 via-50% to-neutral-100"
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  ARTISTS WORLDWIDE
                </motion.span>
              </div>
            </motion.h2>
            
            <motion.p 
              className="px-4 md:text-xl font-light max-w-[577px] m-auto text-black leading-relaxed" 
              style={{ fontFamily: 'system-ui, Avenir, Helvetica, Arial, sans-serif' }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              Discover how Media Nov has transformed the careers of independent artists across the globe with our comprehensive music distribution and rights management services.
            </motion.p>
          </div>

          {/* Story Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto w-full">
            {stories.map((story, index) => (
              <motion.div 
                key={index} 
                className="group relative flex flex-col items-center justify-center rounded-2xl px-4 py-4 text-sm font-medium shadow-[inset_0_-8px_10px_#8fdfff1f] backdrop-blur-sm bg-white text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                whileHover={{ 
                  y: -8,
                  scale: 1.05,
                  transition: { duration: 0.3, type: "spring", stiffness: 300 }
                }}
                whileTap={{ scale: 0.95 }}
              >
                <div className="absolute inset-0 block h-full w-full animate-gradient bg-gradient-to-r from-[#ffaa40]/50 via-[#9c40ff]/50 to-[#ffaa40]/50 bg-[length:300%_100%] p-[1px] ![mask-composite:subtract] [border-radius:inherit] [mask:linear-gradient(#fff_0_0)_content-box,linear-gradient(#fff_0_0)]"></div>
                <div className="relative z-10 flex flex-col items-center">
                  <motion.div 
                    className="w-8 h-8 flex items-center justify-center mb-2"
                    whileHover={{ rotate: 360, scale: 1.2 }}
                    transition={{ duration: 0.5 }}
                  >
                    {story.icon}
                  </motion.div>
                  <motion.h3 
                    className="text-sm font-bold text-black mb-1.5 text-center" 
                    style={{ fontFamily: 'aesthet-nova' }}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 + 0.2 }}
                  >
                    {story.title}
                  </motion.h3>
                  <motion.p 
                    className="text-gray-600 text-xs mb-2 text-center"
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 + 0.3 }}
                  >
                    {story.description}
                  </motion.p>
                  <motion.div 
                    className="flex justify-center space-x-1.5 text-xs flex-wrap gap-1.5"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 + 0.4 }}
                  >
                    {story.badges.map((badge, badgeIndex) => (
                      <span key={badgeIndex} className={`${getBadgeColor(badge.color)} px-2 py-1 rounded-xl text-[10px] text-gray-700 font-medium`}>
                        {badge.text}
                      </span>
                    ))}
                  </motion.div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Button */}
          <motion.div 
            className="text-center mt-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ delay: 0.5 }}
          >
            <motion.a 
              href="/contact" 
              className="inline-flex items-center justify-center px-8 py-4 font-medium rounded-full transition-all duration-500 button-shimmer-dark"
              whileHover={{ scale: 1.1, y: -4 }}
              whileTap={{ scale: 0.95 }}
              transition={{ type: "spring", stiffness: 400, damping: 17 }}
            >
              Start Your Success Story
            </motion.a>
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default SuccessStories;
